import { Layout } from "@/components/common/Layout/Layout";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { FirmSettings } from "@/components/settings";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { ROUTES } from "@/constants/routes";
import { isAdminOrAbove } from '@/lib/permissions';
import { ArrowLeft } from "lucide-react";

export function FirmSettingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const userGroups = user?.groups || [];
  const isAdmin = isAdminOrAbove(userGroups);

  // Redirect if not admin
  useEffect(() => {
    if (!isAdmin) {
      navigate(ROUTES.SETTINGS);
    }
  }, [isAdmin, navigate]);

  // Check for OAuth success/error in URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    
    if (urlParams.has('oauth') && urlParams.get('oauth') === 'success') {
      toast({
        title: "Success",
        description: "Microsoft account connected successfully!",
      });
    }
    
    if (urlParams.has('error')) {
      const description = urlParams.get('description') || 'Unknown error';
      toast({
        title: "Error",
        description: `Failed to connect Microsoft account: ${description}`,
        variant: "destructive",
      });
    }
  }, [location, toast]);

  // Get the current firm ID from the user object
  const getCurrentFirmId = () => {
    return user?.firms[0] || 0;
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <Layout>
      <div className="w-full mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <button
              type="button"
              onClick={() => navigate(ROUTES.SETTINGS)}
              className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Settings
            </button>
            <h1 className="text-2xl font-semibold tracking-tight">Firm Administration</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Configure firm-wide settings, templates, and integrations
            </p>
          </div>
        </div>

        <FirmSettings firmId={getCurrentFirmId().toString()} canModify={isAdmin} />
      </div>
    </Layout>
  );
}
